import React, { useRef, useState, useMemo } from "react";
import { SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Button, Input, Space, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import "./modal_customer_master.css";
import useSWR from "swr";
import axios from "axios";
import { CustomerDataType } from "../../components/hoc_vehicle_master";

interface CustomerType {
    code: string;
    deskripsi: string;
}

interface ModalCustomerMasterProps {
    onCustomerSelect: (customer: CustomerDataType) => void;
}

type DataIndex = keyof CustomerDataType;
const ModalCustomerMaster: React.FC<ModalCustomerMasterProps> = ({ onCustomerSelect }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data: customers, isLoading } = useSWR<CustomerDataType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/customers`, fetcher);
    const { data: customerTypes } = useSWR<CustomerType[]>(`${process.env.REACT_APP_BACKEND_URL}/api/vehicle-master/customer-types`, fetcher);
    const tableData = customers?.map(customer => ({ ...customer, key: customer.cs_code }));

    const customerTypeMap = useMemo(() => {
        if (!customerTypes) return new Map<string, string>();
        return new Map(customerTypes.map(ct => [ct.code, ct.deskripsi]));
    }, [customerTypes]);


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };


    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<CustomerDataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),

        onFilter: (value, record) =>
            (record[dataIndex] ?? '')
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });



    const columns: TableColumnsType<CustomerDataType> = [
        {
            title: 'Customer Code',
            dataIndex: 'cs_code',
            key: 'cs_code',
            sorter: (a, b) => a.cs_code.localeCompare(b.cs_code),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('cs_code'),
            ellipsis: true,
        },
        {
            title: 'Customer Name',
            dataIndex: 'cs_name',
            key: 'cs_name',
            sorter: (a, b) => a.cs_name.localeCompare(b.cs_name),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('cs_name'),
            ellipsis: true,
        },
        {
            title: 'Customer Type',
            dataIndex: 'customer_type',
            key: 'customer_type',
            sorter: (a, b) => {
                const descA = customerTypeMap.get(a.customer_type) || a.customer_type;
                const descB = customerTypeMap.get(b.customer_type) || b.customer_type;
                return descA.localeCompare(descB);
            },
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('customer_type'),
            onFilter: (value, record) => {
                const description = customerTypeMap.get(record.customer_type) || record.customer_type;
                return description.toString().toLowerCase().includes((value as string).toLowerCase());
            },
            render: (text) => {
                const description = customerTypeMap.get(text) || text;
                return searchedColumn === 'customer_type' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={description ? description.toString() : ''}
                    />
                ) : (
                    description
                );
            },
            ellipsis: true,
        },
        {
            title: 'Address 1',
            dataIndex: 'address_line1',
            key: 'address_line1',
            sorter: (a, b) => (a.address_line1 || '').localeCompare(b.address_line1 || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('address_line1'),
            ellipsis: true,
        },
        {
            title: 'Address 2',
            dataIndex: 'address_line2',
            key: 'address_line2',
            sorter: (a, b) => (a.address_line2 || '').localeCompare(b.address_line2 || ''),
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('address_line2'),
            ellipsis: true,
        },
    ];


    return (

        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="in8n">
                <div className="gjs-grid-column" id="idwf">
                    <Table<CustomerDataType>
                        columns={columns}
                        dataSource={tableData}
                        loading={isLoading}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    onCustomerSelect(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ModalCustomerMaster;